import * as THREE from 'three';
import { obstacles, terrainHeightMap } from '../world';
import { frontendLogger } from '../../../shared/logger';
import { PhysicsManager } from '../physics';

/**
 * Load a GLB/GLTF map file
 * Place your .glb files in frontend/public/maps/
 */
export async function loadGLBMap(
  filename: string,
  scene: THREE.Scene,
  physicsManager?: PhysicsManager
): Promise<THREE.Group> {
  frontendLogger.info(`Loading GLB map: ${filename}`);
  
  // Initialize flat terrain for spawn system
  for (let x = 0; x < 64; x++) {
    for (let z = 0; z < 64; z++) {
      terrainHeightMap.set(`${x},${z}`, 0);
    }
  }
  
  // Import GLTFLoader
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  const loader = new GLTFLoader();
  
  return new Promise((resolve, reject) => {
    loader.load(
      `/maps/${filename}`,
      (gltf) => {
        const mapGroup = gltf.scene;
        
        // Center the map at spawn position (32, 0, 32)
        mapGroup.position.set(32, 0, 32);
        
        // Scale if needed (adjust based on your map)
        // mapGroup.scale.setScalar(10); // Uncomment and adjust scale
        
        // Enable shadows
        mapGroup.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            // Add collision detection
            child.updateMatrixWorld(true);
            const box = new THREE.Box3().setFromObject(child);
            obstacles.set(`map_${child.uuid}`, {
              type: 'map',
              bounds: box
            });
            
            // Add to physics world if physics manager is provided
            if (physicsManager) {
              const size = new THREE.Vector3();
              box.getSize(size);
              const center = new THREE.Vector3();
              box.getCenter(center);
              physicsManager.createObstacleBody(center, size, `map_${child.uuid}`);
            }
          }
        });
        
        scene.add(mapGroup);
        frontendLogger.info(`GLB map loaded successfully: ${filename}`);
        resolve(mapGroup);
      },
      (progress) => {
        const percent = (progress.loaded / progress.total) * 100;
        frontendLogger.info(`Loading map: ${percent.toFixed(0)}%`);
      },
      (error) => {
        frontendLogger.error(`Failed to load GLB map: ${filename}`, error);
        reject(error);
      }
    );
  });
}
